import React, { FC, useState, useEffect, useContext } from "react";
import ConfirmationModal from "common/components/dialogs/ConfirmationModal";
import useProjectStore, { IDeviceInstance, DistanceUnit, INetwork } from "common/store/ProjectStore";
import { Box, Grid, Tab, Tabs, Container, IconButton } from "@mui/material";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import useLookupsStore, { IUpdateRate } from "common/store/LookupsStore";
import useDeviceStore from "common/store/DeviceStore";
import { LanguageContext } from "common/contexts/LanguageContext";
import {
  DecimalToUndredthsRegEx,
  FeetMetersConversion,
  ValidInputCharsCrLfRegEx,
  ValidInputCharsRegEx,
} from "common/constants/GlobalValues";
import theme from "common/theme/theme";
import DeviceCategory from "common/constants/DeviceCategory";
import DialogTextField, {
  DialogMultilineTextField,
  DialogNumberField,
} from "common/components/dialogs/DialogTextField";
import DialogDropdownTextField from "common/components/dialogs/DialogDropdownTextField";
import DialogDropdownMenuItem from "common/components/dialogs/DialogDropdownMenuItem";
import useNotificationStore from "common/store/NotificationStore";
import { formatNumber } from "common/utils/formatting";
import { getNetwork, getNetworkAntennaName } from "common/utils/networkExtensions";

interface IProps {
  networkID: number;
  instanceID: number;
  open: boolean;
  setOpen: (b: boolean) => void;
  rangeUpdated: (coordsUpdated: { updated: boolean; index: number }) => void;
  deleteDeviceMeta: (n: number) => void;
}

const DeviceProperties: FC<IProps> = ({ networkID, instanceID, open, setOpen, rangeUpdated, deleteDeviceMeta }) => {
  const [deviceTag, setDeviceTag] = useState<string>("");
  const [range, setRange] = useState<string>("");
  const [rangeList, setRangeList] = useState<number[]>([]);
  const [height, setHeight] = useState<string>("");
  const [tabIndex, setTabIndex] = React.useState(0);
  const [originalInstance, setOriginalInstance] = useState<IDeviceInstance | null>(null);
  const [selectedUpdateRate, setSelectedUpdateRate] = useState<number | null>(null);
  const [deviceUpdateRates, setDeviceUpdateRates] = useState<IUpdateRate[]>([]);
  const [antennaOptions, setAntennaOptions] = useState<INetwork[]>([]);
  const [selectedAntenna, setSelectedAntenna] = useState<number | null>(null);
  const [container, setContainer] = useState<number | null>(null);
  const [note, setNote] = useState<string>("");
  const [isHelpOpen, setIsHelpOpen] = useState<boolean>(false);
  const [hasRange, setHasRange] = useState<boolean>(false);
  const [distanceAbbr, setDistanceAbbr] = useState<string>("");
  const [deviceType, setDeviceType] = useState<number>(-1);

  const {
    updateDeviceInstances,
    distanceID,
    networkContainers,
    activeGateway,
    activeNetworkContainerID,
    activeNetworkContainer,
    updateGateway,
    deleteDeviceInstance,
    addDeviceInstance,
  } = useProjectStore();
  const { ranges, updateRates } = useLookupsStore();
  const { lookupUpdateRate, lookupCategory } = useDeviceStore();
  const { enqueueNotification } = useNotificationStore();

  const { getString } = useContext(LanguageContext);

  useEffect(() => {
    const network = getNetwork(networkID, networkContainers);
    if (instanceID && activeGateway && instanceID !== activeGateway.deviceInstanceID) {
      var origInstance = network?.deviceInstances.find((inst) => inst.deviceInstanceID === instanceID);
    } else {
      origInstance = activeGateway!;
    }

    setOriginalInstance(origInstance || null);
    setRangeList([]);
    ranges.forEach((r) => {
      if (distanceID === DistanceUnit.Feet) {
        setRangeList((prev) => [...prev, r.feet]);
      } else {
        setRangeList((prev) => [...prev, Math.round((r.feet / FeetMetersConversion) * 10) / 10]);
      }
    });
    setDistanceAbbr(getString(distanceID === DistanceUnit.Feet ? "cmn_feet_abbr" : "cmn_meters_abbr"));

    if (origInstance !== undefined) {
      setDeviceTag(origInstance.deviceTag);
      setRange(formatNumber(origInstance.range));
      setHeight(formatNumber(origInstance.height));
      setNote(origInstance.note || "");
    }

    if (network !== undefined) {
      var cont = networkContainers.find((cont) => cont.networkContainerID === network.networkContainerID);
      if (cont) {
        setContainer(cont.networkContainerID);
        setAntennaOptions([...cont.networks]);
      }
    }

    setSelectedAntenna(networkID);
  }, [
    networkContainers,
    networkID,
    instanceID,
    distanceID,
    ranges,
    getString,
    lookupCategory,
    activeGateway,
    activeNetworkContainer,
  ]);

  useEffect(() => {
    if (originalInstance === null) {
      return;
    }
    const updateRateInfo = lookupUpdateRate(originalInstance?.deviceID);
    setSelectedUpdateRate(originalInstance.updateRateID === undefined ? null : originalInstance.updateRateID);
    const filteredUpdateRates = updateRates.filter((rate) => updateRateInfo.updateRateIDs.includes(rate.updateRateID));
    setDeviceUpdateRates(filteredUpdateRates);

    const category = lookupCategory(originalInstance.deviceID);
    setHasRange(category !== DeviceCategory.Gateway);
  }, [lookupUpdateRate, lookupCategory, updateRates, originalInstance]);

  const handleUpdateRateChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setSelectedUpdateRate(parseInt(event.target.value));
  };

  const handleAntennaChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setSelectedAntenna(parseInt(event.target.value));
  };

  const handleNetworkContainerChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setContainer(parseInt(event.target.value));
    var cont = networkContainers.find((cont) => cont.networkContainerID === parseInt(event.target.value));
    if (cont) {
      setAntennaOptions([...cont.networks]);
      setSelectedAntenna(cont.networks[0].networkID);
    }
  };

  useEffect(() => {
    if (open) {
      if (originalInstance !== undefined && originalInstance !== null) {
        setDeviceTag(originalInstance.deviceTag);
        setRange(formatNumber(originalInstance.range));
        setHeight(formatNumber(originalInstance.height));
        setNote(originalInstance.note || "");
        setSelectedUpdateRate(originalInstance.updateRateID === undefined ? null : originalInstance.updateRateID);
        setDeviceType(lookupCategory(originalInstance.deviceID));
      }
    }
  }, [open, originalInstance]);

  const handleHeightChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    // Allow only numeric values
    if (DecimalToUndredthsRegEx.test(inputValue)) {
      setHeight(inputValue);
    }
  };

  const handleRangeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    // Allow only numeric values
    if (DecimalToUndredthsRegEx.test(inputValue)) {
      setRange(inputValue);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const handleNoteChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNote(event.target.value);
  };

  const handleClose = () => {
    setOpen(false);
    setTabIndex(0);
  };

  const propertiesConfirm = () => {
    let invalidChars = false;
    if (deviceTag && !ValidInputCharsRegEx.test(deviceTag)) {
      enqueueNotification("invalid_chars_dt", `${getString("msg_invalid_chars")} ${getString("DeviceTag")}`, "error");
      invalidChars = true;
    }
    if (note && !ValidInputCharsCrLfRegEx.test(note)) {
      enqueueNotification("invalid_chars_n", `${getString("msg_invalid_chars_lf")} ${getString("Note")}`, "error");
      invalidChars = true;
    }
    if (invalidChars) {
      return;
    }

    var updateInstance: IDeviceInstance = { ...originalInstance! };
    updateInstance.deviceTag = deviceTag ?? originalInstance!.deviceTag;
    updateInstance.range = +range;
    updateInstance.height = +height;
    updateInstance.updateRateID = selectedUpdateRate ?? originalInstance?.updateRateID!;
    updateInstance.note = note !== "" ? note : null;
    if (instanceID === activeGateway!.deviceInstanceID) {
      updateGateway(activeNetworkContainerID, updateInstance);
    } else {
      updateDeviceInstances(networkID, updateInstance);
    }
    if (range && originalInstance!.range !== +range) {
      const network = getNetwork(networkID, networkContainers);
      const instanceIndex: number =
        network?.deviceInstances.findIndex((inst) => inst.deviceInstanceID === instanceID) ?? -1;
      rangeUpdated({ updated: true, index: instanceIndex });
    }
    handleClose();
    if (selectedAntenna && selectedAntenna !== networkID) {
      var cont = networkContainers.find((cont) => cont.networkContainerID === container);
      var net = cont?.networks.find((net) => net.networkID === selectedAntenna);
      if (net && net.deviceInstances.length >= 1) {
        updateInstance.rowVersion = null;
        updateInstance.updatedDate = null;
        deleteDeviceMeta(updateInstance.deviceInstanceID);
        addDeviceInstance(updateInstance, selectedAntenna);
        deleteDeviceInstance(updateInstance.deviceInstanceID, networkID);
      } else {
        var message = getString("nwk_rules_antenna_second");
        enqueueNotification(message.replace(/\s/g, ""), message, "error");
      }
    }
  };

  return (
    <>
      <ConfirmationModal
        isOpen={open}
        handleClose={handleClose}
        handleConfirm={propertiesConfirm}
        title={getString("dlg_devprops_title")}
        maxWidth="md"
        hideDivider={true}
      >
        <Container sx={{ pt: "24px" }}>
          <Tabs value={tabIndex} onChange={handleTabChange} indicatorColor="primary" textColor="primary">
            <Tab label={getString("dlg_devprops_tab_detail")} />
            <Tab label={getString("dlg_devprops_tab_note")} />
          </Tabs>
          <Box sx={{ height: "300px", mt: "24px" }}>
            <div hidden={tabIndex !== 0}>
              <Grid container spacing={3}>
                <Grid item xs={8}>
                  {container && (
                    <DialogDropdownTextField
                      label={getString("dlg_devprops_nwk_lbl")}
                      select
                      size="small"
                      variant="outlined"
                      fullWidth
                      value={container}
                      onChange={handleNetworkContainerChange}
                      disabled={
                        networkContainers.length < 2 ||
                        deviceType === -1 ||
                        deviceType === DeviceCategory.Gateway ||
                        deviceType === DeviceCategory.Antenna
                      }
                    >
                      {networkContainers.map((item, index) => (
                        <DialogDropdownMenuItem key={index} value={item.networkContainerID}>
                          {item.networkContainerName}
                        </DialogDropdownMenuItem>
                      ))}
                    </DialogDropdownTextField>
                  )}
                </Grid>
                <Grid item xs={4}>
                  {deviceType !== -1 &&
                    deviceType !== DeviceCategory.Gateway &&
                    deviceType !== DeviceCategory.Antenna &&
                    selectedAntenna && (
                      <DialogDropdownTextField
                        label={getString("dlg_devprops_ant_lbl")}
                        select
                        size="small"
                        variant="outlined"
                        fullWidth
                        value={selectedAntenna}
                        onChange={handleAntennaChange}
                        disabled={antennaOptions.length < 2}
                      >
                        {antennaOptions.map((item, index) => (
                          <DialogDropdownMenuItem key={index} value={item.networkID}>
                            {getNetworkAntennaName(item, lookupCategory, getString("mnu_unnamed_nwk"))}
                          </DialogDropdownMenuItem>
                        ))}
                      </DialogDropdownTextField>
                    )}
                </Grid>
                <Grid item xs={12}>
                  <DialogTextField
                    variant="filled"
                    size="small"
                    label={getString("dlg_devprops_tag_lbl")}
                    fullWidth
                    value={deviceTag}
                    onChange={(e) => setDeviceTag(e.target.value)}
                  />
                </Grid>
                <Grid container item xs={12} spacing={3} sx={{ mt: "-36px" }}>
                  <Grid item xs={4}>
                    <DialogNumberField
                      label={`${getString("dlg_devprops_ht_lbl")} (${distanceAbbr})`}
                      type="text"
                      variant="filled"
                      size="small"
                      fullWidth
                      value={height}
                      onChange={handleHeightChange}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    {hasRange && (
                      <DialogNumberField
                        label={
                          <div>
                            <label>{`${getString("dlg_devprops_rng_lbl")} (${distanceAbbr})`}</label>
                            <IconButton
                              disabled={false}
                              disableRipple
                              sx={{
                                pointerEvents: "auto",
                                width: "12px",
                                height: "12px",
                                ml: "8px",
                                color: theme.palette.primary.main,
                                "&:hover": { color: theme.palette.primary.light },
                              }}
                              onClick={() => {
                                setIsHelpOpen(true);
                              }}
                            >
                              <HelpOutlineOutlinedIcon sx={{ width: "20px", height: "20px" }} />
                            </IconButton>
                          </div>
                        }
                        type="text"
                        variant="filled"
                        size="small"
                        fullWidth
                        value={range}
                        onChange={handleRangeChange}
                      />
                    )}
                  </Grid>
                  <Grid item xs={4} sx={{ mt: "2px" }}>
                    {selectedUpdateRate && selectedUpdateRate > 0 && (
                      <DialogDropdownTextField
                        label={getString("dlg_devprops_upd_rt_lbl")}
                        select
                        size="small"
                        variant="outlined"
                        fullWidth
                        value={selectedUpdateRate}
                        onChange={handleUpdateRateChange}
                      >
                        {deviceUpdateRates.map((item, index) => (
                          <DialogDropdownMenuItem key={index} value={item.updateRateID}>
                            {item.updateRateSeconds}
                          </DialogDropdownMenuItem>
                        ))}
                      </DialogDropdownTextField>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </div>
            <div hidden={tabIndex !== 1}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <DialogMultilineTextField
                    variant="filled"
                    size="small"
                    label={getString("dlg_devprops_note_lbl")}
                    fullWidth
                    multiline
                    rows={10}
                    value={note}
                    onChange={handleNoteChange}
                  />
                </Grid>
              </Grid>
            </div>
          </Box>
        </Container>
      </ConfirmationModal>
      <ConfirmationModal
        isOpen={isHelpOpen}
        handleClose={() => {}}
        handleConfirm={() => setIsHelpOpen(false)}
        confirmBtnLabel={getString("cmn_ok")}
        hideCancel={true}
        hideDivider={true}
        title={getString("dlg_devprops_rng_hlp_ttl")}
        maxWidth="sm"
      >
        <p style={{ marginTop: "20px" }}>{getString("dlg_devprops_rng_hlp_prfx")}</p>
        <div style={{ marginRight: "16px" }}>
          {ranges.map((r, i) => (
            <p key={i}>
              <li>
                {r.rangeName}: {rangeList[i]}{" "}
                {distanceID === DistanceUnit.Feet ? getString("cmn_feet") : getString("cmn_meters")}
              </li>
            </p>
          ))}
          <p>{getString("dlg_devprops_rng_hlp_sufx")}</p>
        </div>
      </ConfirmationModal>
    </>
  );
};

export default DeviceProperties;
